import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';

import { SideNavOuterToolbarComponent } from './layouts/side-nav-outer-toolbar/side-nav-outer-toolbar.component';

import { msalOrBypassGuard } from './guards/msal-or-bypass.guard';
import { permissionsGuard } from './guards/permission-guard';
import { crystalxAccessGuard } from './guards/crystalx-access.guard';
import { unsavedChangesGuard } from './guards/unsaved-changes.guard';
import { AppPermissions, PROVIDER_VIEW_PERMISSIONS } from './routes/permissions';

const routes: Routes = [
  {
    path: '',
    component: SideNavOuterToolbarComponent,
    children: [
      {
        path: 'wip-tracker',
        loadComponent: () => import('./pages/boardroom/wip-tracker/wip-tracker.component').then((m) => m.WipTrackerComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.STATS_REPORTING],
        },
      },
      {
        path: 'board-reports',
        loadComponent: () => import('./pages/boardroom/board-reports/board-reports.component').then((m) => m.BoardReportsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.STATS_REPORTING],
        },
      },
      {
        path: 'time-saved',
        loadComponent: () => import('./pages/boardroom/time-saved/time-saved.component').then((m) => m.TimeSavedComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.STATS_REPORTING, AppPermissions.ANALYTICS_FULL],
        },
      },
      {
        path: 'time-analysis',
        loadComponent: () => import('./pages/boardroom/time-analysis/time-analysis/time-analysis.component').then((m) => m.TimeAnalysisComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.STATS_REPORTING],
        },
      },
      {
        path: 'crm-prospects',
        loadComponent: () => import('./pages/crm/crm-prospects/crm-prospects.component').then((m) => m.CrmProspectsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PROSPECTION,
            AppPermissions.CRM_BASIC,
          ],
        },
      },
      {
        path: 'crm-clients',
        loadComponent: () => import('./pages/crm/crm-clients/crm-clients.component').then((m) => m.CrmClientsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PROSPECTION,
            AppPermissions.CRM_BASIC,
          ],
        },
      },
      {
        path: 'crm-todo',
        loadComponent: () => import('./pages/crm/crm-todo/crm-todo.component').then((m) => m.CrmTodoComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PROSPECTION,
            AppPermissions.CRM_BASIC,
          ],
        },
      },
      {
        path: 'analytics-budget-dashboard',
        loadComponent: () => import('./pages/analytics/analytics-dashboard/analytics-dashboard.component').then((m) => m.AnalyticsDashboardComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.ANALYTICS_FULL],
        },
      },
      {
        path: 'analytics-sales-report',
        loadComponent: () => import('./pages/analytics/analytics-sales-report/analytics-sales-report.component').then((m) => m.AnalyticsSalesDashboardComponent),
        canActivate: [permissionsGuard],
      },
      {
        path: 'analytics-volume-reporting',
        loadComponent: () => import('./pages/boardroom/boardroom-volume-report/boardroom-volume-report.component').then((m) => m.AnalyticsVolumeReportComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.STATS_REPORTING],
        },
      },
      {
        path: 'cost-follow-up',
        loadComponent: () => import('./pages/analytics/analytics-cost-follow-up/analytics-cost-follow-up.component').then((m) => m.AnalyticsCostFollowUpComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.ANALYTICS_FULL],
        },
      },
      {
        path: 'docket-mail-list',
        loadComponent: () => import('./pages/docket-mail-list/docket-mail-list.component').then((m) => m.DocketMailListComponent),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.TIME_REGISTRATION] }, // P244b: same gate as Docketing/Files/* + saveFiles
      },
      {
        path: 'mail-queue',
        loadComponent: () => import('./pages/mail-queue/mail-queue.component').then((m) => m.MailQueueComponent),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.AI_DOCKETING_USE] },
      },
      // P19: the Mail queue is the main list; the AI docketing queue stays reachable as its history view.
      {
        path: 'ai-docketing-queue',
        loadComponent: () => import('./pages/ai-docketing-queue/ai-docketing-queue.component').then((m) => m.AiDocketingQueueComponent),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.AI_DOCKETING_USE] },
      },
      {
        path: 'fto-analysis-new',
        loadComponent: () => import('./pages/fto-analysis-new/fto-analysis-new.component').then((m) => m.FtoAnalysisNewComponent),
        canActivate: [permissionsGuard],
      },
      {
        path: 'fto-analysis',
        loadComponent: () => import('./pages/fto-analysis/fto-analysis.component').then((m) => m.FtoAnalysisComponent),
        canActivate: [permissionsGuard],
      },
      {
        path: 'patentability',
        loadComponent: () => import('./pages/patentability/patentability-list/patentability-list.component').then((m) => m.PatentabilityListComponent),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.CRM_BASIC] },
      },
      {
        path: 'patentability/:id',
        loadComponent: () => import('./pages/patentability/patentability-wizard/patentability-wizard.component').then((m) => m.PatentabilityWizardComponent),
        canActivate: [permissionsGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { requiredPermissions: [AppPermissions.CRM_BASIC] },
      },
      {
        path: 'prior-art-poc',
        redirectTo: 'patentability',
        pathMatch: 'full',
      },
      {
        path: 'prior-art-style',
        loadComponent: () => import('./pages/prior-art-style/prior-art-style.component').then((m) => m.PriorArtStyleComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CRM_CONFIGURATION],
        },
      },
      {
        path: 'ai-docketing-queue/:id',
        loadComponent: () => import('./pages/ai-docketing-detail/ai-docketing-detail.component').then((m) => m.AiDocketingDetailComponent),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.AI_DOCKETING_USE] },
      },
      {
        path: 'docket-file',
        loadComponent: () => import('./pages/docket-file/docket-file.component').then((m) => m.DocketFileComponent),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.TIME_REGISTRATION] }, // P244b: same gate as Docketing/Files/* + saveFiles
      },
      {
        path: 'time-registration-register',
        loadComponent: () => import('./pages/time-registration/time-registration-register-v2/time-registration-register-v2.component').then((m) => m.TimeRegistrationRegisterV2Component),
        canActivate: [permissionsGuard],
      },
      {
        path: 'time-registration-reporting-personal',
        loadComponent: () => import('./pages/time-registration/time-registration-reporting-personal/time-registration-reporting-personal.component').then((m) => m.TimeRegistrationReportingPersonalComponent),
        canActivate: [permissionsGuard],
      },
      {
        path: 'time-registration-reporting-team',
        loadComponent: () => import('./pages/time-registration/time-registration-reporting-team/time-registration-reporting-team.component').then((m) => m.TimeRegistrationReportingTeamComponent),
        canActivate: [permissionsGuard],
      },
      {
        path: 'finance-charges-v2',
        loadComponent: () => import('./pages/finance/finance-charges-overview/finance-charges-overview.component').then((m) => m.FinanceChargesOverviewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.INVOICING_BASIC,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-charges-v2/:clientId/:currencyId',
        loadComponent: () => import('./pages/finance/finance-charges/finance-charges.component').then((m) => m.FinanceChargesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.INVOICING_BASIC,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-invoices-in',
        loadComponent: () => import('./pages/finance/finance-invoices-in/finance-invoices-in.component').then((m) => m.FinanceInvoicesInComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.COST,
            AppPermissions.INVOICING_FULL,
            AppPermissions.EXTERNAL,
          ],
        },
      },
      {
        path: 'finance-invoices-in/new',
        loadComponent: () => import('./pages/finance/finance-invoices-in/invoices-in-detail/invoices-in-detail.component').then((m) => m.InvoicesInDetailComponent),
        canActivate: [permissionsGuard],
        data: {
          mode: 'create',
          requiredPermissions: [
            AppPermissions.COST,
            AppPermissions.INVOICING_FULL,
            AppPermissions.EXTERNAL,
          ],
        },
      },
      {
        path: 'finance-invoices-in/review/:documentAnalysisId',
        loadComponent: () => import('./pages/finance/finance-invoices-in/invoices-in-review/invoices-in-review.component').then((m) => m.InvoicesInReviewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.COST,
            AppPermissions.INVOICING_FULL,
            AppPermissions.EXTERNAL,
          ],
        },
      },
      {
        path: 'finance-invoices-in/:id/edit',
        loadComponent: () => import('./pages/finance/finance-invoices-in/invoices-in-detail/invoices-in-detail.component').then((m) => m.InvoicesInDetailComponent),
        canActivate: [permissionsGuard],
        data: {
          mode: 'edit',
          requiredPermissions: [
            AppPermissions.COST,
            AppPermissions.INVOICING_FULL,
            AppPermissions.EXTERNAL,
          ],
        },
      },
      {
        path: 'finance-invoices-out',
        loadComponent: () => import('./pages/finance/finance-invoices-out/finance-invoices-out.component').then((m) => m.FinanceInvoicesOutComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.INVOICING_BASIC,
            AppPermissions.INVOICING_FULL,
            AppPermissions.EXTERNAL,
          ],
        },
      },
      {
        path: 'finance-invoices-out/:id/detail',
        loadComponent: () => import('./pages/finance/finance-invoices-out/invoice-out-detail/invoice-out-detail.component').then((m) => m.InvoiceOutDetailComponent),
        canActivate: [permissionsGuard],
        canDeactivate: [unsavedChangesGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.INVOICING_BASIC,
            AppPermissions.INVOICING_FULL,
            AppPermissions.EXTERNAL,
          ],
        },
      },
      {
        path: 'finance-rw-follow-up',
        loadComponent: () => import('./pages/finance/finance-rw-follow-up/finance-rw-follow-up.component').then((m) => m.FinanceRwFollowUpComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.SUBSIDIES_BASIC,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-creditcollect',
        loadComponent: () => import('./pages/finance/finance-creditcollect/finance-creditcollect.component').then((m) => m.FinanceCreditcollectComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-payments-out',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-out/finance-payments-out').then((m) => m.FinancePaymentsOut),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PAYMENTS_RECONCILIATION,
          ],
        },
      },
      {
        path: 'finance-payments-in',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-in/finance-payments-in').then((m) => m.FinancePaymentsIn),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PAYMENTS_RECONCILIATION,
          ],
        },
      },
      {
        path: 'finance-payments-parties',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-parties/finance-payments-parties').then((m) => m.FinancePaymentsParties),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PAYMENTS_RECONCILIATION,
          ],
        },
      },
      {
        path: 'finance-payments-bank-connection',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-bank-connection/finance-payments-bank-connection').then((m) => m.FinancePaymentsBankConnection),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PAYMENTS_RECONCILIATION,
          ],
        },
      },
      {
        path: 'finance-payments-bank-connection-compare',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-bank-connection-compare/finance-payments-bank-connection-compare').then((m) => m.FinancePaymentsBankConnectionCompare),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PAYMENTS_BANK_CONNECTION,
          ],
        },
      },
      {
        path: 'finance-payments-category-rules',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-category-rules/finance-payments-category-rules').then((m) => m.FinancePaymentsCategoryRules),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PAYMENTS_RECONCILIATION,
          ],
        },
      },
      {
        path: 'finance-payments-party-ledger',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-party-ledger/finance-payments-party-ledger').then((m) => m.FinancePaymentsPartyLedger),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PAYMENTS_RECONCILIATION,
          ],
        },
      },
      {
        path: 'finance-payments-party-ledger/:paymentPartyId',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-party-ledger/finance-payments-party-ledger').then((m) => m.FinancePaymentsPartyLedger),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PAYMENTS_RECONCILIATION,
          ],
        },
      },
      {
        path: 'finance-payments-runs',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-runs/finance-payments-runs').then((m) => m.FinancePaymentsRuns),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.PAYMENTS_INITIATE] },
      },
      {
        path: 'finance-payments-runs/new',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-run-editor/finance-payments-run-editor').then((m) => m.FinancePaymentsRunEditor),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.PAYMENTS_INITIATE] },
      },
      {
        path: 'finance-payments-runs/:id/edit',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-run-editor/finance-payments-run-editor').then((m) => m.FinancePaymentsRunEditor),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.PAYMENTS_INITIATE] },
      },
      {
        path: 'finance-payments-runs/:id',
        loadComponent: () => import('./pages/finance/finance-payments/finance-payments-run/finance-payments-run').then((m) => m.FinancePaymentsRun),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.PAYMENTS_INITIATE] },
      },
      {
        path: 'finance-payments-bank-details',
        loadComponent: () => import('./pages/finance/finance-payments/bank-details-worklist/bank-details-worklist').then((m) => m.BankDetailsWorklist),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.PAYMENTS_INITIATE] },
      },
      {
        path: 'finance-schedule-of-charges',
        loadComponent: () => import('./pages/finance/finance-schedule-of-charges-v2/finance-schedule-of-charges-v2.component').then((m) => m.FinanceScheduleOfChargesV2Component),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-reporting-bad-payers',
        loadComponent: () => import('./pages/finance/finance-reporting-bad-payers/finance-reporting-bad-payers.component').then((m) => m.FinanceReportingBadPayersComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.BADPAYERS_MANAGEMENT,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-reporting-bad-payers-pebu',
        loadComponent: () => import('./pages/finance/finance-reporting-bad-payers-pebu/finance-reporting-bad-payers-pebu.component').then((m) => m.FinanceReportingBadPayersPEBUComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.BADPAYERS_MANAGEMENT,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-reporting-cost-overview',
        loadComponent: () => import('./pages/finance/finance-reporting-cost-overview/finance-reporting-cost-overview.component').then((m) => m.FinanceReportingCostOverviewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING_REPORTING,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-reporting-cost-item-overview',
        loadComponent: () => import('./pages/finance/finance-reporting-cost-item-overview/finance-reporting-cost-item-overview.component').then((m) => m.FinanceReportingCostItemOverviewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING_REPORTING,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-reporting-invoice-overview',
        loadComponent: () => import('./pages/finance/finance-reporting-invoice-overview/finance-reporting-invoice-overview.component').then((m) => m.FinanceReportingInvoiceOverviewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.INVOICING_REPORTING,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-reporting-invoice-subsidies',
        loadComponent: () => import('./pages/finance/finance-reporting-invoice-subsidies/finance-reporting-invoice-subsidies.component').then((m) => m.FinanceReportingInvoiceSubsidiesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.INVOICING_REPORTING,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-reporting-amadeus-invoice-overview',
        loadComponent: () => import('./pages/finance/finance-reporting-amadeus-invoice-overview/finance-reporting-amadeus-invoice-overview.component').then((m) => m.FinanceReportingAmadeusInvoiceOverviewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.INVOICING_REPORTING,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-reporting-spadel-invoice-overview',
        loadComponent: () => import('./pages/finance/finance-reporting-spadel-invoice-overview/finance-reporting-spadel-invoice-overview.component').then((m) => m.FinanceReportingSpadelInvoiceOverviewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.INVOICING_REPORTING,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-configuration-invoice-categories',
        loadComponent: () => import('./pages/finance/finance-configuration-invoice-categories/finance-configuration-invoice-categories.component').then((m) => m.FinanceConfigurationInvoiceCategoriesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING_CONFIGURATION,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-configuration-invoiceline-categories',
        loadComponent: () => import('./pages/finance/finance-configuration-invoiceline-categories/finance-configuration-invoiceline-categories.component').then((m) => m.FinanceConfigurationInvoiceLineCategoriesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING_CONFIGURATION,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-configuration-pricelist',
        loadComponent: () => import('./pages/finance/finance-configuration-pricelist/finance-configuration-pricelist.component').then((m) => m.FinanceConfigurationPricelistComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.FINANCIAL,
            AppPermissions.INVOICING_CONFIGURATION,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
            {
        path: 'finance-configuration-schedule-of-charges',
        loadComponent: () => import('./pages/finance/finance-schedule-of-charges-v2/finance-schedule-of-charges-v2.component').then((m) => m.FinanceScheduleOfChargesV2Component),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.FINANCIAL,
            AppPermissions.INVOICING_CONFIGURATION,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-configuration-workcode',
        loadComponent: () => import('./pages/finance/finance-configuration-workcode/finance-configuration-workcode.component').then((m) => m.FinanceConfigurationWorkcodeComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.FINANCIAL,
            AppPermissions.WORKCODES_CONFIGURATION,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-configuration-workcode-categories',
        loadComponent: () => import('./pages/finance/finance-configuration-workcode-categories/finance-configuration-workcode-categories.component').then((m) => m.FinanceConfigurationWorkCodeCategoriesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.FINANCIAL,
            AppPermissions.WORKCODES_CONFIGURATION,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-configuration-agent-tarif-review',
        loadComponent: () => import('./pages/finance/finance-configuration-agent-tarif-review/finance-configuration-agent-tarif-review.component').then((m) => m.FinanceConfigurationAgentTarifReviewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.FINANCIAL_PRICEMAINTAINER,
            AppPermissions.INVOICING_CONFIGURATION,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-bank-account-track',
        loadComponent: () => import('./pages/finance/finance-bank-account-track/finance-bank-account-track.component').then((m) => m.FinanceBankAccountTrackComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.BANK_REPORTING,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-bank-account-validation',
        loadComponent: () => import('./pages/finance/finance-bank-account-validation/finance-bank-account-validation.component').then((m) => m.FinanceBankAccountValidationComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.BANK_REPORTING,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'finance-cost-item-budget',
        loadComponent: () => import('./pages/finance/finance-cost-item-budget/finance-cost-item-budget.component').then((m) => m.FinanceCostItemBudgetComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING_REPORTING,
            AppPermissions.INVOICING_FULL,
          ],
        },
      },
      {
        path: 'patricia-client-and-prospects',
        loadComponent: () => import('./pages/patricia/patricia-clients-and-prospects/patricia-clients-and-prospects.component').then((m) => m.BecameClientOrProspectOnComponent),
        canActivate: [permissionsGuard],
      },
      {
        path: 'patricia-events-deadlines',
        loadComponent: () => import('./pages/patricia/patricia-events-deadlines/patricia-events-deadlines.component').then((m) => m.PatriciaEventDeadlinesComponent),
        canActivate: [permissionsGuard],
      },
      {
        path: 'patricia-document-validation',
        loadComponent: () => import('./pages/patricia/patricia-document-validation/patricia-document-validation.component').then((m) => m.PatriciaDocumentValidationComponent),
        canActivate: [permissionsGuard],
        // P244b: same gate as GET api/Patricia/CaseDocumentOverview.
        data: { requiredPermissions: [AppPermissions.CLIENT_PORTAL, AppPermissions.ClIENT_PORTAL_MANAGEMENT_FULL] },
      },
      {
        path: 'provider-list',
        loadComponent: () => import('./pages/providers-list/providers-list.component').then((m) => m.ProviderListComponent),
        canActivate: [permissionsGuard],
        data: {
          // P143: viewing matches GET api/Provider; add/edit/delete is gated inside the page.
          requiredPermissions: PROVIDER_VIEW_PERMISSIONS,
        },
      },
      {
        path: 'renewals-internal-overview',
        loadComponent: () => import('./pages/renewal/renewal-internal-overview/renewal-internal-overview.component').then((m) => m.RenewalInternalOverviewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL],
        },
      },
      {
        path: 'renewals-pavis-import-export',
        loadComponent: () => import('./pages/renewal/renewal-pavis/renewal-pavis-import-export/renewal-pavis-import-export.component').then((m) => m.RenewalPavisImportExportComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewals-ipify-activity',
        loadComponent: () => import('./pages/renewal/renewal-ipify/renewal-ipify-activity-log/renewal-ipify-activity-log.component').then((m) => m.RenewalIpifyActivityLogComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewal-ipify-instruction-log',
        redirectTo: 'renewals-ipify-activity',
        pathMatch: 'full',
      },
      {
        // SendToIpify standalone page removed in Cases rework — its rows
        // surface inside the Cases page as the In-flight bucket, with a
        // per-row "force push" action for queue items not yet pushed by
        // the nightly batch.
        path: 'renewals-ipify-send-to-ipify',
        redirectTo: 'renewals-ipify-cases',
        pathMatch: 'full',
      },
      {
        // Old Instructions URL kept as a redirect to preserve any
        // bookmarks. The page is now called Cases.
        path: 'renewals-ipify-instructions',
        redirectTo: 'renewals-ipify-cases',
        pathMatch: 'full',
      },
      {
        path: 'renewals-ipify-import-export',
        loadComponent: () => import('./pages/renewal/renewal-ipify/renewal-ipify-import-export/renewal-ipify-import-export.component').then((m) => m.RenewalIpifyImportExportComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewals-ipify-invoices',
        loadComponent: () => import('./pages/renewal/renewal-ipify/renewal-ipify-invoices/renewal-ipify-invoices.component').then((m) => m.RenewalIpifyInvoicesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewals-ipify-invoice-detail',
        redirectTo: 'renewals-ipify-invoices',
        pathMatch: 'full',
      },
      {
        path: 'renewals-ipify-client-budget/:clientId',
        loadComponent: () => import('./pages/renewal/renewal-ipify/renewal-ipify-client-budget/renewal-ipify-client-budget.component').then((m) => m.RenewalIpifyClientBudgetComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        // Aggregated per-case detail (Overview reconciliation + Activity).
        // Documents / Budget / Sync / Upcoming-works tabs become live as
        // their parent phases land. Listed before the Cases list route so
        // Angular matches the more specific :id segment first.
        path: 'renewals-ipify-cases/:id',
        loadComponent: () => import('./pages/renewal/renewal-ipify/renewal-ipify-case-detail/renewal-ipify-case-detail.component').then((m) => m.RenewalIpifyCaseDetailComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewals-ipify-cases',
        loadComponent: () => import('./pages/renewal/renewal-ipify/renewal-ipify-cases/renewal-ipify-cases.component').then((m) => m.RenewalIpifyCasesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewals-ipify-documents',
        loadComponent: () => import('./pages/renewal/renewal-ipify/renewal-ipify-documents/renewal-ipify-documents.component').then((m) => m.RenewalIpifyDocumentsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewals-pavis-send-to-pavis',
        loadComponent: () => import('./pages/renewal/renewal-pavis/renewal-pavis-send-to-pavis/renewal-pavis-send-to-pavis.component').then((m) => m.RenewalPavisSendToPavisComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewals-pavis-instructions',
        loadComponent: () => import('./pages/renewal/renewal-pavis/renewal-pavis-instructions/renewal-pavis-instruction.component').then((m) => m.RenewalPavisInstructionComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewals-pavis-invoice',
        loadComponent: () => import('./pages/renewal/renewal-pavis/renewal-pavis-invoice-details/renewal-pavis-invoice-details.component').then((m) => m.RenewalPavisInvoiceDetailsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewals-pavis-log-instruction-log',
        loadComponent: () => import('./pages/renewal/renewal-instruction-log/renewal-instruction-log.component').then((m) => m.RenewalPavisInstructionLogComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewals-pavis-log-response-log',
        loadComponent: () => import('./pages/renewal/renewal-response-log/renewal-response-log.component').then((m) => m.RenewalPavisResponseLogComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWAL, AppPermissions.PAVIS],
        },
      },
      {
        path: 'renewals-pavis-configuration-event',
        loadComponent: () => import('./pages/renewal/renewal-configuration/renewal-configuration.component').then((m) => m.RenewalConfigurationComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWALS_CONFIGURATION],
        },
      },
      {
        path: 'renewal-configuration/edit/:id',
        loadComponent: () => import('./pages/renewal/renewal-configuration/renewal-configuration-modify/renewal-configuration-modify.component').then((m) => m.RenewalConfigurationModifyComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWALS_CONFIGURATION],
        },
      },
      {
        path: 'renewal-configuration/edit/:configId/line/:lineId',
        loadComponent: () => import('./pages/renewal/renewal-configuration/event-configuration-line-detail/event-configuration-line-detail.component').then((m) => m.EventConfigurationLineDetailComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.RENEWALS_CONFIGURATION],
        },
      },
      {
        path: 'time-registration-configuration-billing-type',
        loadComponent: () => import('./pages/time-registration/time-registration-configuration-billing-types/time-registration-configuration-billing-types.component').then((m) => m.TimeRegistrationConfigurationBillingTypesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.TIMEREGISTRATION_CONFIGURATION],
        },
      },
      {
        path: 'time-registration-configuration-employee-target',
        loadComponent: () => import('./pages/time-registration/time-registration-configuration-employee-target/time-registration-configuration-employee-target.component').then((m) => m.TimeRegistrationConfigurationEmployeeTargetComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.TIMEREGISTRATION_MANAGEMENT],
        },
      },
      {
        path: 'time-registration-configuration-team-target',
        loadComponent: () => import('./pages/time-registration/time-registration-configuration-team-target/time-registration-configuration-team-target.component').then((m) => m.TimeRegistrationConfigurationTeamTargetComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.TIMEREGISTRATION_MANAGEMENT,
            AppPermissions.TEAMS_MANAGER,
          ],
        },
      },
      {
        path: 'admin-portal-languages',
        loadComponent: () => import('./pages/portal-admin/portal-admin-language/portal-admin-language.component').then((m) => m.AdminPortalLanguageComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.GENERAL_MANAGEMENT],
        },
      },
      {
        path: 'admin-portal-service',
        loadComponent: () => import('./pages/portal-admin/portal-admin-service/portal-admin-service.component').then((m) => m.AdminPortalServiceComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.GENERAL_MANAGEMENT],
        },
      },
      {
        path: 'admin-portal-template-engine-bookmark',
        loadComponent: () => import('./pages/portal-admin/portal-admin-template-engine-bookmarks/portal-admintemplate-engine-bookmarks.component').then((m) => m.AdminPortalTemplateEngineBookmarkComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.TEMPLATE_ENGINE_CONFIGURATION,
          ],
        },
      },
      {
        path: 'admin-portal-template-engine-templates',
        loadComponent: () => import('./pages/portal-admin/portal-admin-template-engine-template/portal-admin-template-engine-template.component').then((m) => m.AdminPortalTemplateComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.TEMPLATE_ENGINE_CONFIGURATION,
          ],
        },
      },
      {
        path: 'admin-portal-template-engine-template-type',
        loadComponent: () => import('./pages/portal-admin/portal-admin-template-engine-template-type/portal-admin-template-engine-template-type.component').then((m) => m.AdminPortalTemplateTypeComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.TEMPLATE_ENGINE_CONFIGURATION,
          ],
        },
      },
      {
        path: 'admin-portal-template-engine-template-bookmark',
        loadComponent: () => import('./pages/portal-admin/portal-admin-template-engine-template-bookmark/portal-admin-template-engine-template-bookmark.component').then((m) => m.AdminPortalTemplateBookmarkComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.INVOICING,
            AppPermissions.TEMPLATE_ENGINE_CONFIGURATION,
          ],
        },
      },
      {
        path: 'budget-configuration-administration-category',
        loadComponent: () => import('./pages/budget/budget-configuration-administration-category/budget-configuration-administration-category.component').then((m) => m.BudgetConfigurationAdministrationCategoryComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.BUDGET_MANAGEMENT],
        },
      },
      {
        path: 'budget-configuration-administration-subcategory',
        loadComponent: () => import('./pages/budget/budget-configuration-administration-subcategory/budget-configuration-administration-subcategory.component').then((m) => m.BudgetConfigurationAdministrationSubCategoryComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.BUDGET_MANAGEMENT],
        },
      },
      {
        path: 'budget-configuration-figures-sales-target',
        loadComponent: () => import('./pages/budget/budget-configuration-figures-sales-target/budget-configuration-figures-sales-target.component').then((m) => m.BudgetConfigurationFigureSalesTargetComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.BUDGET_MANAGEMENT],
        },
      },
      {
        path: 'administration-car-overview',
        loadComponent: () => import('./pages/administration/administration-car-overview/administration-car-overview.component').then((m) => m.AdministrationCarOverviewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.COMPANY_MANAGEMENT_FULL],
        },
      },
      {
        path: 'administration-car-brands',
        loadComponent: () => import('./pages/administration/administration-car-brand/administration-car-brand.component').then((m) => m.AdministrationCarBrandComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.COMPANY_MANAGEMENT_FULL],
        },
      },
      {
        path: 'administration-car-fuel-types',
        loadComponent: () => import('./pages/administration/administration-car-fuelType/administration-car-fuelType.component').then((m) => m.AdministrationCarFuelTypeComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.COMPANY_MANAGEMENT_FULL],
        },
      },
      {
        path: 'administration-car-insurance-companies',
        loadComponent: () => import('./pages/administration/administration-car-insurance-company/administration-car-insurance-company.component').then((m) => m.AdministrationCarInsuranceCompanyComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.COMPANY_MANAGEMENT_FULL],
        },
      },
      {
        path: 'administration-car-leasing-companies',
        loadComponent: () => import('./pages/administration/administration-car-leasing-companies/administration-car-leasing-companies.component').then((m) => m.AdministrationCarLeasingCompanyComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.COMPANY_MANAGEMENT_FULL],
        },
      },
      {
        path: 'administration-employee-employees/:id',
        loadComponent: () => import('./pages/administration/administration-employee-detail/administration-employee-detail.component').then((m) => m.AdministrationEmployeeDetailComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.COMPANY_MANAGEMENT_FULL,
            AppPermissions.EMPLOYEE_MANAGEMENT,
          ],
        },
      },
      {
        path: 'administration-employee-employees',
        loadComponent: () => import('./pages/administration/administration-employee/administration-employee.component').then((m) => m.AdministrationEmployeeComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.COMPANY_MANAGEMENT_FULL,
            AppPermissions.EMPLOYEE_MANAGEMENT,
          ],
        },
      },
      {
        path: 'administration-employee-employee-salary',
        loadComponent: () => import('./pages/administration/administration-employee-salaries/administration-employee-salaries.component').then((m) => m.AdministrationEmployeeSalariesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.COMPANY_MANAGEMENT_FULL,
            AppPermissions.EMPLOYEE_MANAGEMENT,
          ],
        },
      },
      {
        path: 'administration-countries',
        loadComponent: () => import('./pages/administration/administration-countries/administration-countries.component').then((m) => m.AdministrationCountriesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.COMPANY_MANAGEMENT_FULL,
            AppPermissions.COMPANY_MANAGEMENT_BASIC,
          ],
        },
      },
      {
        path: 'administration-currency',
        loadComponent: () => import('./pages/administration/administration-currency/administration-currency.component').then((m) => m.AdministrationCurrencyComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.COMPANY_MANAGEMENT_FULL,
            AppPermissions.COMPANY_MANAGEMENT_BASIC,
          ],
        },
      },
      {
        path: 'administration-computers',
        loadComponent: () => import('./pages/administration/administration-computers/administration-computers.component').then((m) => m.AdministrationComputerComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.COMPANY_MANAGEMENT_FULL,
            AppPermissions.COMPANY_MANAGEMENT_BASIC,
          ],
        },
      },
      {
        path: 'administration-job-health',
        loadComponent: () => import('./pages/administration/administration-job-health/administration-job-health.component').then((m) => m.AdministrationJobHealthComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.ADMIN],
        },
      },
      {
        path: 'sender-domains',
        loadComponent: () =>
          import('./pages/sender-domains/sender-domains.component').then((m) => m.SenderDomainsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.ADMIN],
        },
      },
      {
        path: 'roles-permissions',
        loadComponent: () =>
          import('./pages/administration/roles-permissions/roles-permissions-page.component').then(
            (m) => m.RolesPermissionsPageComponent,
          ),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.ADMIN],
        },
      },
      {
        path: 'client-portal-account-px-follow-up',
        loadComponent: () => import('./pages/client-portal/client-portal-account-px-follow-up/client-portal-account-px-follow-up.component').then((m) => m.AccountFollowUpPatriciaComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.CLIENT_PORTAL,
            AppPermissions.ClIENT_PORTAL_MANAGEMENT_FULL,
          ],
        },
      },
      {
        path: 'client-portal-case-type-mapping',
        loadComponent: () => import('./pages/client-portal/client-portal-case-type-mapping/client-portal-case-type-mapping.component').then((m) => m.ClientPortalCaseTypeMappingComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.ClIENT_PORTAL_MANAGEMENT_FULL],
        },
      },
      {
        path: 'client-portal-instructions-mapping',
        loadComponent: () => import('./pages/client-portal/client-portal-instructions-mapping/client-portal-instructions-mapping.component').then((m) => m.ClientPortalInstructionMappingComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.CLIENT_PORTAL,
            AppPermissions.ClIENT_PORTAL_MANAGEMENT_FULL,
          ],
        },
      },
      {
        path: 'client-portal-instructions-action-text',
        loadComponent: () => import('./pages/client-portal/client-portal-instructions-action-text/client-portal-instructions-action-text.component').then((m) => m.ClientPortalInstructionActionTextComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.CLIENT_PORTAL,
            AppPermissions.ClIENT_PORTAL_MANAGEMENT_FULL,
          ],
        },
      },
      {
        path: 'client-portal-instructions-categories',
        loadComponent: () => import('./pages/client-portal/client-portal-instructions-workflow-categories/client-portal-instructions-workflow-categories.component').then((m) => m.ClientPortalInstructionWorkFlowCategoryComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.CLIENT_PORTAL,
            AppPermissions.ClIENT_PORTAL_MANAGEMENT_FULL,
          ],
        },
      },
      {
        path: 'client-portal-instructions-response-types',
        loadComponent: () => import('./pages/client-portal/client-portal-instructions-response-type/client-portal-instructions-response-type.component').then((m) => m.ClientPortalInstructionResponseTypeComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.CLIENT_PORTAL,
            AppPermissions.ClIENT_PORTAL_MANAGEMENT_FULL,
          ],
        },
      },
      {
        path: 'client-portal-instructions-status-text',
        loadComponent: () => import('./pages/client-portal/client-portal-instructions-status-text/client-portal-instructions-status-text.component').then((m) => m.ClientPortalInstructionStatusTextComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.CLIENT_PORTAL,
            AppPermissions.ClIENT_PORTAL_MANAGEMENT_FULL,
          ],
        },
      },
      {
        path: 'client-portal-segments-case-segments',
        loadComponent: () => import('./pages/client-portal/client-portal-segments-case-segments/client-portal-segments-case-segments.component').then((m) => m.ClientPortalCaseSegmentsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.CLIENT_PORTAL,
            AppPermissions.ClIENT_PORTAL_MANAGEMENT_FULL,
          ],
        },
      },
      {
        path: 'client-portal-segments-client-segments',
        loadComponent: () => import('./pages/client-portal/client-portal-segments-client-segments/client-portal-segments-client-segments.component').then((m) => m.ClientPortalClientSegmentsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.CLIENT_PORTAL,
            AppPermissions.ClIENT_PORTAL_MANAGEMENT_FULL,
          ],
        },
      },
      {
        path: 'client-portal-subsidy-brochures',
        loadComponent: () => import('./pages/client-portal/client-portal-subsidy-brochures/client-portal-subsidy-brochures.component').then((m) => m.ClientPortalSubsidyBrochuresComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.CLIENT_PORTAL,
            AppPermissions.ClIENT_PORTAL_MANAGEMENT_FULL,
          ],
        },
      },
      {
        path: 'conversion/batches',
        loadComponent: () => import('./pages/conversion/batches/conversion-batches.component').then((m) => m.ConversionBatchesComponent),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.CASESCONVERSION_INFO] },
      },
      {
        path: 'conversion/batches/:id',
        loadComponent: () => import('./pages/conversion/batch-wizard/conversion-batch-wizard.component').then((m) => m.ConversionBatchWizardComponent),
        canActivate: [permissionsGuard],
        data: { requiredPermissions: [AppPermissions.CASESCONVERSION_INFO] },
      },
      {
        path: 'conversion-case-basic-info',
        loadComponent: () => import('./pages/conversion/case/conversion-case-basic-info/conversion-case-basic-info.component').then((m) => m.ConversionBasicInfoComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CASESCONVERSION_INFO],
        },
      },
      {
        path: 'conversion-case-diary',
        loadComponent: () => import('./pages/conversion/case/conversion-case-diary/conversion-case-diary.component').then((m) => m.ConversionCaseDiaryComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CASESCONVERSION_INFO],
        },
      },
      {
        path: 'conversion-case-trm-class',
        loadComponent: () => import('./pages/conversion/case/conversion-case-trm-class/conversion-case-trm-class.component').then((m) => m.ConversionCaseTrmClassComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CASESCONVERSION_INFO],
        },
      },
      {
        path: 'conversion-case-party',
        loadComponent: () => import('./pages/conversion/case/conversion-case-parties/conversion-case-parties.component').then((m) => m.ConversionCasePartiesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CASESCONVERSION_INFO],
        },
      },
      {
        path: 'crm-client-overview/:id',
        loadComponent: () => import('./pages/crm/crm-clients/crm-clients-overview/crm-clients-overview.component').then((m) => m.CrmClientsOverviewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CRM_CONFIGURATION],
        },
      },
      {
        path: 'crm-client-edit/:id',
        loadComponent: () => import('./pages/crm/crm-clients/crm-clients-edit/crm-clients-edit.component').then((m) => m.CrmClientsEditComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CRM_CONFIGURATION],
        },
      },
      {
        path: 'crm-prospect-new',
        loadComponent: () => import('./pages/crm/crm-prospects/crm-prospect-new/crm-prospect-new.component').then((m) => m.CrmProspectNewComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PROSPECTION,
            AppPermissions.CRM_BASIC,
          ],
        },
      },
      {
        path: 'crm-prospect-edit/:id',
        loadComponent: () => import('./pages/crm/crm-prospects/crm-prospect-edit/crm-prospect-edit.component').then((m) => m.CrmProspectEditComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PROSPECTION,
            AppPermissions.CRM_BASIC,
          ],
        },
      },
      {
        path: 'crm-pipeline',
        loadComponent: () => import('./pages/crm/crm-pipeline/crm-pipeline.component').then((m) => m.CrmPipelineComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PROSPECTION,
            AppPermissions.CRM_BASIC,
          ],
        },
      },
      {
        path: 'crm-opportunity/:id',
        loadComponent: () => import('./pages/crm/crm-opportunity-detail/crm-opportunity-detail.component').then((m) => m.CrmOpportunityDetailComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PROSPECTION,
            AppPermissions.CRM_BASIC,
          ],
        },
      },
      {
        path: 'crm-configuration-action',
        loadComponent: () => import('./pages/crm/configuration/crm-configuration-actions/crm-configuration-actions.component').then((m) => m.CrmConfigurationActionComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CRM_CONFIGURATION],
        },
      },
      {
        path: 'crm-configuration-origin',
        loadComponent: () => import('./pages/crm/configuration/crm-configuration-origin/crm-configuration-origin.component').then((m) => m.CrmConfigurationOriginComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CRM_CONFIGURATION],
        },
      },
      {
        path: 'crm-configuration-pme',
        loadComponent: () => import('./pages/crm/configuration/crm-configuration-pme/crm-configuration-pme.component').then((m) => m.CrmConfigurationPmeComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CRM_CONFIGURATION],
        },
      },
      {
        path: 'crm-configuration-pole',
        loadComponent: () => import('./pages/crm/configuration/crm-configuration-pole/crm-configuration-pole.component').then((m) => m.CrmConfigurationPoleComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CRM_CONFIGURATION],
        },
      },
      {
        path: 'crm-configuration-segmentation',
        loadComponent: () => import('./pages/crm/configuration/crm-configuration-segmentation/crm-configuration-segmentation.component').then((m) => m.CrmConfigurationSegmentationComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CRM_CONFIGURATION],
        },
      },
      {
        path: 'crm-configuration-prospect-action',
        loadComponent: () => import('./pages/crm/configuration/crm-configuration-prospect-action/crm-configuration-prospect-action.component').then((m) => m.CrmConfigurationProspectActionComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [
            AppPermissions.PROSPECTION,
            AppPermissions.CRM_CONFIGURATION,
          ],
        },
      },
      {
        path: 'crm-configuration-field-status',
        loadComponent: () => import('./pages/crm/configuration/crm-configuration-field-status/crm-configuration-field-status.component').then((m) => m.CrmConfigurationFieldStatusComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.CRM_CONFIGURATION],
        },
      },
      {
        path: 'views-cases-not-imported-in-pavis',
        loadComponent: () => import('./pages/renewal/renewal-views-case-not-imported-in-pavis/renewal-views-case-not-imported-in-pavis.component').then((m) => m.RenewalViewCaseNotImportedInPavisComponent),
        canActivate: [permissionsGuard],
      },
      {
        path: 'fee-calculator',
        loadComponent: () => import('./pages/fee-calculator/fee-calculator.component').then((m) => m.FeeCalculatorComponent),
        //canActivate: [MsalGuard],
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.FEE_CALCULATOR],
        },
      },
      {
        path: 'fee-calculator-wizard/:id',
        loadComponent: () => import('./pages/fee-calculator/fee-calculator-wizard/fee-calculator-wizard.component').then((m) => m.FeeCalculatorWizardComponent),
        //canActivate: [MsalGuard],
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.FEE_CALCULATOR],
        },
      },
      {
        path: 'fee-calculator-configuration-billing-matrix',
        loadComponent: () => import('./pages/fee-calculator/fee-calculator-configuration/billing-matrix/billing-matrix.component').then((m) => m.FeeCalculatorConfigurationBillingMatrixComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.FEE_CALCULATOR_CONFIGURATION],
        },
      },
      {
        path: 'fee-calculator-configuration-billing-matrix/add',
        loadComponent: () => import('./pages/fee-calculator/fee-calculator-configuration/billing-matrix/billing-matrix-add/billing-matrix-add.component').then((m) => m.BillingMatrixAddComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.FEE_CALCULATOR_CONFIGURATION],
        },
      },
      {
        path: 'fee-calculator-configuration-billing-matrix/modify/:id',
        loadComponent: () => import('./pages/fee-calculator/fee-calculator-configuration/billing-matrix/billing-matrix-modify/billing-matrix-modify.component').then((m) => m.BillingMatrixModifyComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.FEE_CALCULATOR_CONFIGURATION],
        },
      },
      {
        path: 'fee-calculator-configuration-terms-and-conditions',
        loadComponent: () => import('./pages/fee-calculator/fee-calculator-configuration/terms-and-conditions/terms-and-conditions.component').then((m) => m.TermsAndConditionsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.FEE_CALCULATOR_CONFIGURATION],
        },
      },
      {
        path: 'fee-calculator-configuration-service-types',
        loadComponent: () => import('./pages/fee-calculator/fee-calculator-configuration/service-types/service-types.component').then((m) => m.ServiceTypesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.FEE_CALCULATOR_CONFIGURATION],
        },
      },
      {
        path: 'epo-communications',
        loadComponent: () => import('./pages/epo-communications/epo-communications.component').then((m) => m.EpoCommunicationsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.EPO],
        },
      },
      {
        path: 'data-quality-px-parties',
        loadComponent: () => import('./pages/consistency-checks/data-quality/data-quality-px-parties/data-quality-px-parties.component').then((m) => m.DataQualityPxPartiesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'data-quality-px-diary',
        loadComponent: () => import('./pages/consistency-checks/data-quality/data-quality-px-diary/data-quality-px-diary.component').then((m) => m.DataQualityPxDiaryComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'data-quality-px-actions',
        loadComponent: () => import('./pages/consistency-checks/data-quality/data-quality-px-actions/data-quality-px-actions.component').then((m) => m.DataQualityPxActionsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'security-px-actions',
        loadComponent: () => import('./pages/consistency-checks/security/security-px-actions/security-px-actions.component').then((m) => m.SecurityPxActionsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'security-px-annuity',
        loadComponent: () => import('./pages/consistency-checks/security/security-px-annuity/security-px-annuity.component').then((m) => m.SecurityPxAnnuityComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'security-px-names',
        loadComponent: () => import('./pages/consistency-checks/security/security-px-names/security-px-names.component').then((m) => m.SecurityPxNamesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'security-px-parties',
        loadComponent: () => import('./pages/consistency-checks/security/security-px-parties/security-px-parties.component').then((m) => m.SecurityPxPartiesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'security-px-diary',
        loadComponent: () => import('./pages/consistency-checks/security/security-px-diary/security-px-diary.component').then((m) => m.SecurityPxDiaryComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'data-quality-px-names',
        loadComponent: () => import('./pages/consistency-checks/data-quality/data-quality-px-names/data-quality-px-names.component').then((m) => m.DataQualityPxNamesComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'data-quality-px-annuity',
        loadComponent: () => import('./pages/consistency-checks/data-quality/data-quality-px-annuity/data-quality-px-annuity.component').then((m) => m.DataQualityPxAnnuityComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'data-quality-px-basic',
        loadComponent: () => import('./pages/consistency-checks/data-quality/data-quality-px-basic/data-quality-px-basic.component').then((m) => m.DataQualityPxBasicComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'data-quality-px-financial',
        loadComponent: () => import('./pages/consistency-checks/data-quality/data-quality-px-financial/data-quality-px-financial.component').then((m) => m.DataQualityPxFinancialComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        // P259: Data health, the landing page of the Consistency checks menu entry. Renewal (9) alone sees the Renewals rules.
        path: 'consistency-checks',
        loadComponent: () => import('./pages/consistency-checks/data-health/data-health.component').then((m) => m.DataHealthComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY, AppPermissions.RENEWAL],
        },
      },
      {
        path: 'consistency-checks-user-logs',
        loadComponent: () => import('./pages/consistency-checks/consistency-checks-user-logs/data-quality-user-logs.component').then((m) => m.ConsistencyChecksUserLogsComponent),
        canActivate: [permissionsGuard],
        data: {
          requiredPermissions: [AppPermissions.DATA_QUALITY],
        },
      },
      {
        path: 'ai-assistant/:sessionId',
        loadComponent: () => import('./pages/ai-assistant/ai-assistant-page.component').then((m) => m.AiAssistantPageComponent),
        canActivate: [permissionsGuard],
      },
      {
        path: 'ai-assistant',
        loadComponent: () => import('./pages/ai-assistant/ai-assistant-page.component').then((m) => m.AiAssistantPageComponent),
        canActivate: [permissionsGuard],
      },
      {
        path: 'home',
        loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent),
        //todo should be chnaged to permission guard later
        // To check later on.
        canActivate: [msalOrBypassGuard],
      },
      {
        path: 'crystalx/matters/:id',
        loadComponent: () =>
          import('./pages/crystalx/matter-overview/matter-overview.component').then(
            (m) => m.MatterOverviewComponent,
          ),
        canActivate: [permissionsGuard, crystalxAccessGuard],
      },
      {
        path: 'crystalx/search',
        loadComponent: () =>
          import('./pages/crystalx/matter-search/matter-search.component').then((m) => m.MatterSearchComponent),
        canActivate: [permissionsGuard, crystalxAccessGuard],
      },
      {
        path: 'crystalx/paralegals',
        loadComponent: () =>
          import('./pages/crystalx/virtual-paralegal-queue/virtual-paralegal-queue.component').then(
            (m) => m.VirtualParalegalQueueComponent,
          ),
        canActivate: [permissionsGuard, crystalxAccessGuard],
      },
      {
        path: 'crystalx/attention',
        loadComponent: () =>
          import('./pages/crystalx/attorney-attention/attorney-attention.component').then(
            (m) => m.AttorneyAttentionComponent,
          ),
        canActivate: [permissionsGuard, crystalxAccessGuard],
        data: {
          requiredPermissions: [AppPermissions.MATTER_ATTENTION],
        },
      },
      {
        path: 'crystalx/client-reports/review',
        loadComponent: () =>
          import('./pages/crystalx/client-reports-to-review/client-reports-to-review.component').then(
            (m) => m.ClientReportsToReviewComponent,
          ),
        canActivate: [permissionsGuard, crystalxAccessGuard],
        data: {
          requiredPermissions: [AppPermissions.CRYSTALX_ACCESS],
        },
      },
      {
        path: 'crystalx/client-reports/text-blocks',
        loadComponent: () =>
          import('./pages/crystalx/client-report-text-blocks/client-report-text-blocks.component').then(
            (m) => m.ClientReportTextBlocksComponent,
          ),
        canActivate: [permissionsGuard, crystalxAccessGuard],
        data: {
          requiredPermissions: [AppPermissions.ADMIN],
        },
      },
      {
        path: 'crystalx/rule-engine-candidates',
        loadComponent: () =>
          import('./pages/crystalx/rule-engine-queue/rule-engine-queue.component').then(
            (m) => m.RuleEngineQueueComponent,
          ),
        canActivate: [permissionsGuard, crystalxAccessGuard],
        data: {
          requiredPermissions: [AppPermissions.CRYSTALX_RULE_QUEUE_READ],
        },
      },
      {
        path: '**',
        redirectTo: '/home',
        pathMatch: 'full',
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: true,
      bindToComponentInputs: true,
    }),
    BrowserModule,
  ],
  providers: [],
  exports: [RouterModule],
  declarations: [],
})
export class AppRoutingModule {}
